import { Controller } from "@hotwired/stimulus";
import { setCriteriaField } from "./utils/criteria_fields";
import { applyFiltersForm } from "./utils/filters_form";
import { applyTaxProgramToggle } from "./utils/tax_program";

// simulations (Stimulus controller) — gestion des simulations enregistrées du
// simulateur widget (#529, Phase 2a de l'épic #300).
//
// Contrairement au mode « Profils » (auto-save des critères), le mode
// « Simulations » n'a PAS d'auto-save : la sauvegarde est explicite (clic
// « Enregistrer »). Le bloc « Nom de la simulation », premier bloc de la colonne
// profil depuis #572, porte :
// - un champ « Nom de la simulation »,
// - un bouton « Ouvrir une simulation » (ouvre, DANS la colonne profil, le
//   panneau listant les simulations enregistrées — il recouvre donc ce bloc),
// - un bouton « Enregistrer » (inactif tant qu'aucun bien n'est chargé).
//
// Le contrôleur est monté sur le shell `.simulator-widget-shell` (ancêtre commun
// de la colonne profil et de la colonne détails) : ses cibles restent ainsi dans
// son scope, tout comme l'empty state retiré par `exitEntryScreen()`.
// En revanche, le recalcul `api-sync` et le `toggle:set` de fiscalité doivent
// être émis sur `.simulator-panel` (qui porte les contrôleurs `api-sync` /
// `toggle`), résolu explicitement via `apiSyncElement()`.
export default class extends Controller {
    static targets = ["panel", "column", "list", "status", "save", "title", "feedback", "entryScreen"];

    static values = {
        listUrl: String,
        saveUrl: String,
        openUrlTemplate: String,
        // Vrai lorsqu'on arrive sur le widget sans lot (`simulations_entry_screen?`)
        // : la colonne profil affiche alors d'emblée le panneau « Mes simulations »
        // (la classe `is-simulations-open` est déjà posée côté serveur), on charge
        // donc la liste dès le `connect()`.
        entryScreen: Boolean,
    };

    connect() {
        this.simulations = [];
        // Identifiant de la simulation actuellement chargée : pilote
        // « Enregistrer » (mise à jour) vs création. Renseigné UNIQUEMENT par une
        // ouverture réussie (`open()`) ou le retour d'un enregistrement (`save()`).
        // Consulter puis fermer la liste ne doit rien changer à cet état (#529, 2e
        // passe) : un simple chargement de page repart donc de « aucune ouverte ».
        this.loadedSimulationId = null;
        this.onKeydown = (event) => {
            if (event.key === "Escape") this.closePanel();
        };
        // Efface le feedback « enregistrée » dès qu'une modification est faite dans
        // le formulaire de profil : le message ne doit pas survivre à un changement
        // d'état (il laisserait croire que l'état affiché est enregistré). Binding en
        // capture, à l'image de `api_sync_controller#onAnyInput`.
        this.onProfileInput = () => this.clearFeedback();
        this.bindProfileFormFeedback();
        this.refreshSaveState();

        // Écran d'entrée (aucun lot) : ouvrir le panneau et charger la liste.
        if (this.entryScreenValue) this.openPanel();
    }

    disconnect() {
        document.removeEventListener("keydown", this.onKeydown);
        this.unbindProfileFormFeedback();
    }

    bindProfileFormFeedback() {
        const form = document.getElementById("edit-profile-form");
        if (!form) return;
        this.boundProfileForm = form;
        form.addEventListener("input", this.onProfileInput, true);
        form.addEventListener("change", this.onProfileInput, true);
    }

    unbindProfileFormFeedback() {
        const form = this.boundProfileForm;
        if (!form) return;
        form.removeEventListener("input", this.onProfileInput, true);
        form.removeEventListener("change", this.onProfileInput, true);
        this.boundProfileForm = null;
    }

    // ---------- Ouverture / fermeture du panneau ----------
    // Le panneau REMPLACE le contenu de la colonne profil (pas d'overlay) : on
    // pose une classe d'état sur la colonne, le CSS masque le reste du contenu.
    openPanel(event) {
        if (event) event.preventDefault();
        if (this.hasColumnTarget) this.columnTarget.classList.add("is-simulations-open");
        document.addEventListener("keydown", this.onKeydown);
        this.loadSimulations();
    }

    closePanel(event) {
        if (event) event.preventDefault();
        if (this.hasColumnTarget) this.columnTarget.classList.remove("is-simulations-open");
        document.removeEventListener("keydown", this.onKeydown);
    }

    // ---------- Chargement de la liste ----------
    loadSimulations() {
        if (!this.hasListUrlValue || !this.listUrlValue) return;

        this.setStatus("Chargement…");
        if (this.hasListTarget) this.listTarget.innerHTML = "";

        fetch(this.listUrlValue, {
            headers: { Accept: "application/json" },
            credentials: "same-origin",
        })
            .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
            .then((json) => {
                this.simulations = Array.isArray(json.simulations) ? json.simulations : [];
                // On n'adopte volontairement PAS d'identifiant « chargé » depuis la
                // liste : consulter la liste ne doit pas marquer une simulation comme
                // ouverte (sinon « Enregistrer » l'écraserait au lieu d'en créer une).
                this.renderSimulations(this.simulations);
            })
            .catch(() => this.setStatus("Impossible de charger vos simulations."));
    }

    renderSimulations(simulations) {
        if (!this.hasListTarget) return;
        this.listTarget.innerHTML = "";

        if (simulations.length === 0) {
            this.setStatus("Aucune simulation enregistrée pour le moment.");
            return;
        }

        this.clearStatus();
        simulations.forEach((simulation) => this.listTarget.appendChild(this.buildCard(simulation)));
    }

    // Carte d'une simulation : libellé, date de dernière ouverture, fiscalité,
    // ville / lot / programme dérivés du lot.
    buildCard(simulation) {
        const card = document.createElement("div");
        card.className = "simulator-simulations-card";
        card.dataset.simulationId = String(simulation.id);
        card.setAttribute("role", "button");
        card.setAttribute("tabindex", "0");
        card.dataset.action = "click->simulations#open keydown->simulations#openOnKey";
        // Mise en évidence de la simulation actuellement chargée (cf.
        // `highlightLoadedCard`). `loadedSimulationId` provient UNIQUEMENT de
        // `open()` / `save()`, jamais de la session ni du JSON de liste (#529).
        if (this.loadedSimulationId != null && String(simulation.id) === String(this.loadedSimulationId)) {
            card.classList.add("is-loaded");
        }

        const name = document.createElement("span");
        name.className = "simulator-simulations-card-name small";
        name.textContent = simulation.label || "Simulation sans nom";
        card.appendChild(name);

        const meta = document.createElement("p");
        meta.className = "simulator-simulations-card-meta";
        meta.textContent = this.metaLine(simulation);
        card.appendChild(meta);

        // Résumé du financement du snapshot (« Apport de 20 000 € · Prêt de 20 ans
        // à 3,5% »), formaté côté serveur. Omis si aucun segment n'est renseigné.
        if (simulation.financing_label) {
            const financing = document.createElement("p");
            financing.className = "simulator-simulations-card-financing";
            financing.textContent = simulation.financing_label;
            card.appendChild(financing);
        }

        if (simulation.last_opened_at_label) {
            const opened = document.createElement("p");
            opened.className = "simulator-simulations-card-opened";
            opened.textContent = simulation.last_opened_at_label;
            card.appendChild(opened);
        }

        return card;
    }

    metaLine(simulation) {
        const listing = simulation.listing || {};
        const parts = [
            simulation.tax_program_label,
            listing.city,
            listing.program,
        ].filter((part) => part && String(part).length > 0);
        return parts.join(" · ");
    }

    // ---------- Ouverture d'une simulation ----------
    // Applique les critères (colonne profil) ET le lot (champ caché `listing_id`),
    // quitte l'empty state, puis déclenche UN SEUL recalcul `api-sync` — de sorte
    // que la colonne « Détail de l'investissement » se mette à jour. Le panneau
    // reste OUVERT (#529, 3e passe) : on peut enchaîner les ouvertures pour comparer
    // des simulations, la carte chargée étant mise en évidence en direct.
    open(event) {
        const card = event.currentTarget;
        const id = card && card.dataset ? card.dataset.simulationId : null;
        if (!id || !this.hasOpenUrlTemplateValue) return;

        this.post(this.buildUrl(this.openUrlTemplateValue, id))
            .then((json) => {
                if (!json) return;
                this.clearFeedback();
                this.loadedSimulationId = json.id;
                if (this.hasTitleTarget && json.label) this.titleTarget.value = json.label;
                this.applyCriterias(json.criterias || {});
                this.applyListing(json.listing);
                this.exitEntryScreen();
                this.refreshSaveState();
                this.refreshSimulation();
                this.highlightLoadedCard();
            })
            .catch(() => this.setStatus("Impossible d'ouvrir cette simulation."));
    }

    openOnKey(event) {
        if (event.key !== "Enter" && event.key !== " " && event.key !== "Spacebar") return;
        event.preventDefault();
        this.open(event);
    }

    // ---------- Enregistrement explicite ----------
    save(event) {
        if (event) event.preventDefault();
        const listingId = this.currentListingId();
        if (!listingId) {
            this.showFeedback("Chargez un bien avant d'enregistrer la simulation.", false);
            return;
        }

        const body = {
            listing_id: listingId,
            label: this.hasTitleTarget ? this.titleTarget.value : null,
            simulation_id: this.loadedSimulationId,
            criterias: this.collectCriterias(),
        };

        if (this.hasSaveTarget) this.saveTarget.disabled = true;

        fetch(this.saveUrlValue, {
            method: "POST",
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                "X-CSRF-Token": this.csrfToken(),
            },
            credentials: "same-origin",
            body: JSON.stringify(body),
        })
            .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
            .then((json) => {
                this.loadedSimulationId = json.id;
                this.showFeedback(json.message || "Votre simulation a été enregistrée.", true);
                this.highlightLoadedCard();
            })
            .catch(() => this.showFeedback("Impossible d'enregistrer la simulation.", false))
            .finally(() => this.refreshSaveState());
    }

    // ---------- Application des critères / du lot ----------
    // Aligne l'application des critères sur `profiles#applyResponse` : binding des
    // champs canoniques puis répercussion de la fiscalité (mode résidence /
    // investissement, `toggle:set`, bouton de fiscalité, comparateur). NE déclenche
    // PAS de recalcul ici — un unique `refreshSimulation()` est appelé par `open()`
    // après application des critères ET du lot.
    applyCriterias(criterias) {
        const form = document.getElementById("edit-profile-form");
        if (form) {
            Object.keys(criterias).forEach((key) => setCriteriaField(form, key, criterias[key]));
            applyTaxProgramToggle(this.apiSyncElement(), form, criterias);
        }
        applyFiltersForm(criterias);
    }

    // Renseigne le champ caché `listing_id` du lot de la simulation. NE déclenche
    // PAS de recalcul (cf. `applyCriterias`).
    applyListing(listing) {
        if (!listing || listing.id == null) return;
        document.querySelectorAll('[name="listing_id"]').forEach((field) => {
            field.value = String(listing.id);
        });
    }

    // Retire l'empty state « Aucune simulation ouverte » de la colonne détails :
    // les blocs de détail (restés dans le DOM) redeviennent visibles.
    exitEntryScreen() {
        const details = this.element.querySelector(".simulator-column--details");
        if (details) details.classList.remove("is-simulations-entry");
    }

    // Critères courants du formulaire simulateur, sérialisés en `{ champ: valeur }`.
    collectCriterias() {
        const form = document.getElementById("edit-profile-form");
        const criterias = {};
        if (!form) return criterias;

        const data = new FormData(form);
        for (const [name, value] of data.entries()) {
            const match = name.match(/^criterias\[([^\]]+)\](\[\])?$/);
            if (!match) continue;
            const key = match[1];
            if (match[2]) {
                criterias[key] = criterias[key] || [];
                criterias[key].push(value);
            } else {
                criterias[key] = value;
            }
        }
        return criterias;
    }

    currentListingId() {
        const field = document.querySelector('[name="listing_id"]');
        return field && field.value ? field.value : null;
    }

    // Active « Enregistrer » dès qu'un bien est chargé, sinon le laisse inactif.
    refreshSaveState() {
        if (!this.hasSaveTarget) return;
        this.saveTarget.disabled = !this.currentListingId();
    }

    // Recalcul `listing_details` : émis sur `.simulator-panel` (contrôleur
    // `api-sync`), résolu explicitement car le contrôleur est monté sur le shell
    // parent — un `dispatchEvent` sur `this.element` ne traverserait pas
    // `.simulator-panel` (l'écouteur est posé sur son propre élément).
    refreshSimulation() {
        const target = this.apiSyncElement();
        if (!target) return;
        try {
            target.dispatchEvent(
                new CustomEvent("api-sync:refresh", {
                    bubbles: true,
                    detail: { scope: "listing_details", force: true },
                })
            );
        } catch (_) {}
    }

    apiSyncElement() {
        return document.querySelector('[data-controller~="api-sync"]');
    }

    // ---------- Utilitaires ----------
    post(url) {
        if (!url) return Promise.resolve(null);
        return fetch(url, {
            method: "POST",
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                "X-CSRF-Token": this.csrfToken(),
            },
            credentials: "same-origin",
        }).then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))));
    }

    buildUrl(template, id) {
        return String(template).replace("__ID__", encodeURIComponent(id));
    }

    csrfToken() {
        const meta = document.querySelector('meta[name="csrf-token"]');
        return meta ? meta.getAttribute("content") : "";
    }

    setStatus(message) {
        if (!this.hasStatusTarget) return;
        this.statusTarget.textContent = message;
        this.statusTarget.classList.remove("d-none");
    }

    clearStatus() {
        if (!this.hasStatusTarget) return;
        this.statusTarget.textContent = "";
        this.statusTarget.classList.add("d-none");
    }

    showFeedback(message, success) {
        if (!this.hasFeedbackTarget) return;
        this.feedbackTarget.textContent = message;
        this.feedbackTarget.classList.remove("d-none", "text-success", "text-danger");
        this.feedbackTarget.classList.add(success ? "text-success" : "text-danger");
    }

    clearFeedback() {
        if (!this.hasFeedbackTarget) return;
        this.feedbackTarget.textContent = "";
        this.feedbackTarget.classList.add("d-none");
        this.feedbackTarget.classList.remove("text-success", "text-danger");
    }

    // Repositionne la mise en évidence (`is-loaded`) sur la carte de la simulation
    // actuellement chargée, sans recharger la liste — permet de déplacer le
    // surlignage en direct quand on enchaîne les ouvertures, panneau ouvert.
    highlightLoadedCard() {
        if (!this.hasListTarget) return;
        this.listTarget.querySelectorAll(".simulator-simulations-card").forEach((card) => {
            const match =
                this.loadedSimulationId != null &&
                card.dataset.simulationId === String(this.loadedSimulationId);
            card.classList.toggle("is-loaded", match);
        });
    }
}
